// © Microsoft Corporation. All rights reserved.

import { ImageServiceConfig } from "../services/ImageServiceConfig";
import { isInteger } from "lodash-es";
import { canUseDOM } from "@msnews/isomorphic-util";

/** The cached device pixel ratio. This might not match window.devicePixelRatio as it could be bucketed into a standard size to promote easier caching. */
let cachedDevicePixelRatio: number;

/**
 * Specifies the focal region for the image.
 */
export interface ImageFocalRegion {
    /** The x coordinate of the left side of the focal region. */
    x1: number;

    /** The y coordinate of the top of the focal region. */
    y1: number;

    /** The x coordinate of the right side of the focal region. */
    x2: number;

    /** The y coordinate of the bottom of the focal region. */
    y2: number;
}

/** image format used for generating image url */
export enum ImageFormat {
    JPG = "jpg",
    PNG = "png"
}

/**
 * Contains sizing information needed to generate an image URL.
 */
export interface ImageUrlGenerationInfo {
    /** The width of the image that should be generated, in device independent pixels. */
    width?: number;

    /** The height of the image that should be generated, in device independent pixels. */
    height?: number;

    /** the format of the image. E.g. jpg or png */
    format?: ImageFormat;

    /** The focal region for the image. */
    focalRegion?: ImageFocalRegion;

    /** True to enable DPI scaling and get better looking images when the screen DPI/zoom level lets us. */
    enableDpiScaling: boolean;

    /** The image resize mode. */
    mode?: ImageResizeMode;

    /** The image quality. */
    quality?: number;

    /** The background color to use when letterboxing. */
    backgroundColor?: string;

    /** The device pixel ratio that we get from the request context, must be present when enableDpiScaling is true */
    devicePixelRatio?: number;
}

/**
 * Image resize mode enum
 */
export enum ImageResizeMode {
    /**
     * Default value but the Image Resizer actually replaces it by the scale option (2)
     */
    None = 0,

    /**
     * Maintains aspect ratio as letter box
     */
    Letterbox = 1,

    /**
     * Maintains aspect ratio
     */
    Scale = 2,

    /**
     * Stretches image to fit height and width
     */
    Stretch = 3,

    /**
     * Crops image to fit height and width
     */
    Crop = 4,

    /**
     * Crops the image using focal points
     */
    FocalCrop = 5,

    /**
     * Crop the image using a face auto-detection technique.
     */
    FacialCrop = 6
}

/**
 * Match the image quality of Bing for content images.
 */
export const HighEndImageQuality = 90;

/**
 * Generates an image URL from an ID and sizing info.
 * @param id The ID of the image. e.g. "BB113a2G"
 * @param info Sizing information to generate the URL.
 * @returns The image URL.
 */
export function generateImageUrlFromId(id: string, info: ImageUrlGenerationInfo): string {
    return generateImageUrlFromBaseUrl(ImageServiceConfig.prod + id, info);
}

/**
 * Generates an image URL from a base URL.
 * NOTE: Url must match what is generated in WW.
 * @param baseUrl The base URL for the image, with no query string. e.g. "https://img-s-msn-com.akamaized.net/tenant/amp/entityid/BB12hxvq.img"
 * @param info Sizing information to generate the URL.
 * @returns The image URL.
 */
export function generateImageUrlFromBaseUrl(baseUrl: string, info: ImageUrlGenerationInfo): string {
    const { focalRegion, backgroundColor } = info;

    // Passing width or height 0 to image resizer will cause it to ignore that dimension and size on the other one
    let { width = 0, height = 0, format = ImageFormat.JPG } = info;

    let auxQsp = "";
    if (focalRegion) {
        const focusX = midPoint(focalRegion.x1, focalRegion.x2);
        const focusY = midPoint(focalRegion.y1, focalRegion.y2);

        if (focusX != null && focusY != null) {
            auxQsp += `&x=${focusX}&y=${focusY}`;
        }
    }

    if (backgroundColor) {
        auxQsp += `&b=${backgroundColor}`;
    }

    // m=6 is facial crop. If a focal crop area is provided, we will add the x and y parameters, which will override the facial crop and do a manual focal crop.
    const mode: number = info.mode == null ? 6 : info.mode;

    // Image resizer has a bug where upscaling is not enabled by default, so we need to force it with the query param.
    if (mode === 6) {
        // NOTE: WW appends this separately NOT as part of auxQsp.
        auxQsp += "&u=t";
    }

    if (info.enableDpiScaling) {
        if (!info.devicePixelRatio) {
            throw new Error("A valid devicePixelRatio value must be provided when DPI scaling is enabled");
        }
        // If we have info about the device pixel ratio, convert these device independent pixels into physical pixels.
        const pixelRatio: number = adjustDevicePixelRatio(info.devicePixelRatio);
        if (pixelRatio) {
            width = Math.round(width * pixelRatio);
            height = Math.round(height * pixelRatio);
        }
    }

    const quality: number = info.quality || 60;

    // Image resizer documentation: https://microsoft.sharepoint.com/teams/publishing_infrastructure/_layouts/15/Doc.aspx?sourcedoc={5a9c9f56-4762-47a6-8cd3-c0146bc9bf9a}&action=view&wd=target%28Develop.one%7C3340d642-e2bf-4bdd-9b63-42c479572962%2FImage%20Resizer%20Parameters%7Cdeb5ddfd-d569-45e1-8c21-ed41cecd8f91%2F%29
    return `${baseUrl}?w=${width}&h=${height}&q=${quality}&m=${mode}&f=${format}${auxQsp}`;
}

/**
 * Adjusts the device pixel ratio to use for generating image URLs.
 * @param contextPixelRatio - The device pixel ratio value we get from requestContext
 * @returns The adjusted device pixel ratio.
 */
function adjustDevicePixelRatio(contextPixelRatio: number): number {
    if (canUseDOM() && cachedDevicePixelRatio) {
        return cachedDevicePixelRatio;
    }
    if (contextPixelRatio < 1) {
        return null;
    }

    let adjustedPixelRatio = contextPixelRatio;
    // Special case the x.25 / x.5 / x.75 values, they are always OK
    if (!isInteger(adjustedPixelRatio * 4)) {
        // If not special cased, round up to nearest tenth.
        adjustedPixelRatio = Math.ceil(adjustedPixelRatio * 10) / 10;
    }

    // Cap at 3x
    if (adjustedPixelRatio > 3) {
        adjustedPixelRatio = 3;
    }

    if (canUseDOM()) {
        cachedDevicePixelRatio = adjustedPixelRatio;
    }

    return adjustedPixelRatio;
}

/**
 * Returns the midpoint between two min/max coordinate points.
 * @param min - the min coordinate
 * @param max - the max coordinate
 * @returns the midpoint between min and max.
 */
function midPoint(min: number, max: number): number {
    if (min != null && max != null) {
        return Math.floor((min + max) / 2);
    }
}

/**
 * Create ImageUrlGenerationInfo with height and width params based on the desired width and width/height ratios. If the base image size does
 * not match any ratios wihout stretching the image, use the given fallback width and ratio.
 * @param baseImageSize - height and width of the base image
 * @param desiredDisplayWidth - width we would like the image to span if it can match one of the given primaryImageRatios
 * @param desiredImageRatios - list of width/height ratios. We will try to match one of these ratios otherwise we'll use fallbackImageRatio
 * @param fallbackDisplayWidth - if a ratio in primaryImageRatios cannot be matched, we'll use this value for the images width
 * @param fallbackImageRatio - if a ratio primaryImageRatios cannot be matched, we'll use this value for the images width/height ratio
 * @returns a ImageUrlGenerationInfo with height and width set along with enableDpiScaling set to true and mode set to FacialCrop
 */
export function createUrlInfoGivenDesiredWidthAndRatio(
    baseImageHeight: number,
    baseImageWidth: number,
    desiredDisplayWidth: number,
    desiredImageRatios: number[],
    fallbackDesiredWidth: number,
    fallbackImageRatio: number,
    focalRegion?: ImageFocalRegion): ImageUrlGenerationInfo {
    const urlInfo = {
        enableDpiScaling: true,
        mode: focalRegion ? ImageResizeMode.FocalCrop : ImageResizeMode.FacialCrop,
        height: 0,
        width: 0,
        focalRegion: focalRegion
    };

    let minRatioDiff;

    if (desiredDisplayWidth <= baseImageWidth) {
        // we don't want to lose image quality to fit the desired displayWidth.
        const baseImageRatio = baseImageWidth/baseImageHeight;

        for (let i = 0; i < desiredImageRatios.length; i++) {
            const ratioDiff = Math.abs(baseImageRatio - desiredImageRatios[i]);

            if (ratioDiff < 0.2) {
                const newHeight = Math.floor(desiredDisplayWidth/desiredImageRatios[i]);

                if (newHeight <= baseImageHeight
                    && (minRatioDiff === undefined || ratioDiff < minRatioDiff)) {
                    // Make sure we're not losing image quality to meet the required height based on the disired displayWidth
                    // Also, if we match multiple ratios with the 0.2 error, make sure to match the ratio closest to the base dimenions
                    urlInfo.height = newHeight;
                    urlInfo.width = desiredDisplayWidth;
                    minRatioDiff = ratioDiff;
                }
            }
        }
    }

    if (urlInfo.height > 0) {
        return urlInfo;
    } else {
        urlInfo.height =  Math.floor(fallbackDesiredWidth/fallbackImageRatio);
        urlInfo.width = fallbackDesiredWidth;
        return urlInfo;
    }
}